// src/pages/workloads/WorkloadOverviewPage.tsx
import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  MapPin,
  Users,
  CheckCircle,
  Clock,
  Home,
  AlertCircle,
  Activity,
  Calendar,
  Loader2,
  ArrowUp,
  ArrowDown,
  Minus,
  Folder,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { workloadService } from '../../services/workload.service';
import { assignmentService } from '../../services/assignment.service';
import { WorkloadStatus } from '../../types/workload.types';
import toast from 'react-hot-toast';

interface OverviewStats {
  totalWorkloads: number;
  workloadsByStatus: Record<WorkloadStatus, number>;
  totalAddresses: number;
  completedAddresses: number;
  pendingAddresses: number;
  completionRate: number;
  recentActivity: Array<{
    id: string;
    action: string;
    workloadName: string;
    performedBy: string;
    timestamp: Date;
  }>;
}

const WorkloadOverviewPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { selectedProjectId, selectedProject } = useProject();
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month' | 'all'>('week');

  // Load stats for selected project
  useEffect(() => {
    const loadStats = async () => {
      if (!currentUser || !selectedProjectId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Get workload stats
        const workloadStats = await workloadService.getStats(selectedProjectId);

        // Get recent activity
        const logs = await assignmentService.getProjectLogs(
          selectedProjectId,
          undefined,
          10
        );

        setStats({
          totalWorkloads: workloadStats.total,
          workloadsByStatus: workloadStats.byStatus,
          totalAddresses: workloadStats.totalAddresses,
          completedAddresses: workloadStats.completedAddresses,
          pendingAddresses: workloadStats.totalAddresses - workloadStats.completedAddresses,
          completionRate: workloadStats.completionRate,
          recentActivity: logs.slice(0, 5).map((log) => ({
            id: log.id,
            action: log.action,
            workloadName: log.workloadName,
            performedBy: log.performedByName,
            timestamp: log.performedAt.toDate(),
          })),
        });
      } catch (error) {
        console.error('Error loading stats:', error);
        toast.error('Failed to load overview data');
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, [currentUser, selectedProjectId, timeRange]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-900">Unable to load overview</h2>
          <p className="text-gray-500 mt-2">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="h-7 w-7 mr-3 text-indigo-600" />
            Workload Overview
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor your canvassing progress and performance
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Project indicator */}
          {selectedProject && (
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-md">
              <Folder className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">{selectedProject.name}</span>
            </div>
          )}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as typeof timeRange)}
            className="border border-gray-300 rounded-md px-4 py-2"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Total Workloads */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Workloads</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {stats.totalWorkloads}
              </p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg">
              <MapPin className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-green-600 flex items-center">
              <ArrowUp className="h-4 w-4 mr-1" />
              {stats.workloadsByStatus.active} active
            </span>
          </div>
        </div>

        {/* Total Addresses */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Addresses</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {stats.totalAddresses.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Home className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            {stats.pendingAddresses.toLocaleString()} pending
          </div>
        </div>

        {/* Completed */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {stats.completedAddresses.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            {stats.completionRate}% completion rate
          </div>
        </div>

        {/* Completion Rate */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completion Rate</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {stats.completionRate}%
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <Activity className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-3">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-600 transition-all"
                style={{ width: `${stats.completionRate}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Workload Status Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Workload Status Breakdown
          </h3>
          <div className="space-y-4">
            {[
              { status: 'active', label: 'Active', color: 'bg-green-500' },
              { status: 'paused', label: 'Paused', color: 'bg-yellow-500' },
              { status: 'draft', label: 'Draft', color: 'bg-gray-400' },
              { status: 'completed', label: 'Completed', color: 'bg-blue-500' },
              { status: 'archived', label: 'Archived', color: 'bg-gray-300' },
            ].map(({ status, label, color }) => {
              const count = stats.workloadsByStatus[status as WorkloadStatus] || 0;
              const percentage = stats.totalWorkloads > 0
                ? (count / stats.totalWorkloads) * 100
                : 0;

              return (
                <div key={status}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">{label}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {count}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${color} transition-all`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h3>
          {stats.recentActivity.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No recent activity</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stats.recentActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Activity className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{activity.performedBy}</span>
                      {' '}
                      <span className="text-gray-600">{activity.action}</span>
                      {' '}
                      <span className="font-medium">{activity.workloadName}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {activity.timestamp.toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Homes Remaining */}
      <div className="mt-6 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold opacity-90">Homes Remaining</h3>
            <p className="text-4xl font-bold mt-2">
              {stats.pendingAddresses.toLocaleString()}
            </p>
            <p className="text-sm opacity-75 mt-1">
              addresses left to canvas
            </p>
          </div>
          <div className="text-right">
            <div className="inline-flex items-center px-3 py-1 bg-white/20 rounded-full text-sm">
              <TrendingUp className="h-4 w-4 mr-1" />
              {stats.completionRate}% complete
            </div>
            <p className="mt-4 text-sm opacity-75">
              {stats.completedAddresses.toLocaleString()} of {stats.totalAddresses.toLocaleString()} completed
            </p>
          </div>
        </div>
        <div className="mt-4 h-3 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-white transition-all"
            style={{ width: `${stats.completionRate}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkloadOverviewPage;
